<template>
  <div class="pr-1 sm:overflow-auto md:overflow-hidden lg:overflow-hidden">
    <KeyFigureCards
      :stats="keyFigureStats"
      heading="Team key figures, last 30 days" />

    <TableWithCondensedContent
      class="mt-8"
      has-cell-link
      cell-link-text="Go to project"
      table-placeholder-text="No upcoming or ongoing travels found."
      :table-headers="['Client / PM', 'Project / Budget', 'Proposal Heading', 'Start date - End date']"
      :table-rows="teamProposalsUpcoming.filter((proposal) => (proposal.visible && proposal.project.status !== 'archived')).map((proposal) => {
        return [
          [
            proposal.client.name,
            proposal.project.manager.name,
          ],
          [
            proposal.project.name ?? '-',
            proposal.data.destination.name + ' &bull; ' + (proposal.project.total_travelers + ' pax' ?? '-') + ' &times; ' + (formatCurrency(proposal.project.budget_per_traveller) ?? '-'),
          ],
          [
            proposal.title ?? '-',
            proposal.data.destination.heading ?? '-',
          ],
          (proposal.start_date? getDateTimeInDestinationTimezone(proposal.start_date, proposal.data.destination.timezone, 'DD.MM.YYYY') : '-')
            + ' - '
            + (proposal.end_date ? getDateTimeInDestinationTimezone(proposal.end_date, proposal.data.destination.timezone, 'DD.MM.YYYY') : '-'),
          route('projects.show', {id: proposal.project.id}),
        ];
      })">
      <template #header>
        <Tooltip
          align="left"
          content-class="text-xs italic text-gray-400 hover:text-gray-400 -mb-4 w-max">
          <template #trigger>
            <h2 class="relative w-fit cursor-help text-lg font-medium text-gray-800">
              Upcoming or ongoing travels
            </h2>
          </template>
          <template #content>
            Showing up to 10 trips organised by {{ team.name }} that are currently ongoing, or will start or end within 7 days from today, listed by their start date.
          </template>
        </Tooltip>
      </template>
    </TableWithCondensedContent>

    <TableWithCondensedContent
      class="mt-8"
      has-cell-link
      cell-link-text="Go to proposal"
      :table-headers="['Client', 'Project / Budget', 'Proposal Heading', 'Updated / Starts', 'Status']"
      sort-key="Updated / Starts"
      sort-order="desc"
      :table-rows="userProposalsRecent.filter((proposal) => (proposal.visible && proposal.project.status !== 'archived')).map((proposal) => {
        return [
          proposal.client.name,
          [
            proposal.project.name ?? '-',
            proposal.data.destination.name + ' &bull; ' + (proposal.project.total_travelers + ' pax' ?? '-') + ' &times; ' + (formatCurrency(proposal.project.budget_per_traveller) ?? '-'),
          ],
          [
            proposal.title ?? '-',
            proposal.data.destination.heading ?? '-',
          ],
          [
            (proposal.updated_at ? getDateTimeInCurrentTimezone(proposal.updated_at, 'DD.MM.YYYY') : '-'),
            '&nbsp;&nbsp;&nbsp;' + (proposal.start_date ? getDateTimeInDestinationTimezone(proposal.start_date, proposal.data.destination.timezone, 'DD.MM.YYYY') : '-'),
          ],
          {
            type: 'status-badge',
            value: proposal.status,
          },
          route('proposals.show', {id: proposal.id}),
        ];
      })">
      <template #header>
        <Tooltip
          align="left"
          content-class="text-xs italic text-gray-400 hover:text-gray-400 -mb-4 w-max">
          <template #trigger>
            <h2 class="relative w-fit cursor-help text-lg font-medium text-gray-800">
              Your active proposals
            </h2>
          </template>
          <template #content>
            Showing up to 10 of your most recently updated proposals within the last 60 days, excluding those from archived projects.
          </template>
        </Tooltip>
      </template>
    </TableWithCondensedContent>

    <TableWithCondensedContent
      class="my-8"
      has-cell-link
      cell-link-text="Go to project"
      :table-headers="['Client', 'Project / Manager', 'Pax &times; Budget p.p.', 'Created', 'Status']"
      sort-key="Created"
      sort-order="desc"
      :table-rows="teamProjectsLatest.filter((project) => (project.status !== 'archived')).map((project) => {
        return [
          project.client.name,
          [
            project.name,
            project.manager.name,
          ],
          (project.total_travelers ?? '-') + ' &times; ' + (formatCurrency(project.budget_per_traveller) ?? '-'),
          getDateTimeInCurrentTimezone(project.created_at, 'DD.MM.YYYY'),
          {
            type: 'status-badge',
            value: project.status,
          },
          route('projects.show', {id: project.id}),
        ];
      })">
      <template #header>
        <Tooltip
          align="left"
          content-class="text-xs italic text-gray-400 hover:text-gray-400 -mb-4 w-max">
          <template #trigger>
            <h2 class="relative w-fit cursor-help text-lg font-medium text-gray-800">
              Your team's latest projects
            </h2>
          </template>
          <template #content>
            Showing up to 10 of the most recent projects in <strong>{{ team.name }}</strong> (within the last 60 days), excluding archived projects.
          </template>
        </Tooltip>
      </template>
      <template #button>
        <div class="self-end sm:ml-16 sm:flex-none">
          <button
            type="button"
            class="absolute right-0 top-0 inline-flex items-center justify-center gap-1 rounded-md
              border border-transparent bg-transparent px-4 py-2 text-sm font-medium text-indigo-600 underline shadow-none
              hover:bg-indigo-700 hover:text-white hover:no-underline
              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            @click="showCreateForm = !showCreateForm">
            Add new project
          </button>
        </div>
      </template>
    </TableWithCondensedContent>
  </div>
  <div class="relative mt-5 overflow-hidden rounded-lg bg-white shadow" />
  <Modal
    :show="showCreateForm"
    max-width="md"
    :closeable="true"
    @close="showCreateForm = false">
    <CreateProjectForm @close="showCreateForm = false" />
  </Modal>
  <div class="relative mt-5 overflow-hidden rounded-lg bg-white shadow" />
</template>

<script setup>
/* external */
import {
  ref, useAttrs,
} from 'vue';
import {
  FolderOpenIcon, DocumentPlusIcon, DocumentCheckIcon,
} from '@heroicons/vue/24/outline';
import Modal from '@/Jetstream/Modal.vue';

/* local */
import KeyFigureCards from '@/Components/Cards/KeyFigureCards.vue';
import TableWithCondensedContent from '@/Components/Lists/TableWithCondensedContent.vue';
import {
  formatDateTimeToLocaleDateString, formatCurrency, getDateTimeInDestinationTimezone, getDateTimeInCurrentTimezone,
} from '@/utils';
import Tooltip from '@/Components/Tooltip.vue';
import CreateProjectForm from '@/Components/CreateProjectForm.vue';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */
const props = defineProps({
  teamProposalsUpcoming: {
    type: Array,
    required: true,
    default: () => [],
  },
  userProposalsRecent: {
    type: Array,
    required: true,
    default: () => [],
  },
  teamProjectsLatest: {
    type: Array,
    required: true,
    default: () => [],
  },
  teamProjectsActiveCountForLastTwoPeriods: {
    type: Object,
    required: true,
    default: () => ({
      current_period_count: 0,
      previous_period_count: 0,
    }),
  },
  teamProposalsCreatedCountForLastTwoPeriods: {
    type: Object,
    required: true,
    default: () => ({
      current_period_count: 0,
      previous_period_count: 0,
    }),
  },
  teamProposalsVersionsAcceptedCountForLastTwoPeriods: {
    type: Object,
    required: true,
    default: () => ({
      current_period_count: 0,
      previous_period_count: 0,
    }),
  },
});

const attrs = useAttrs();
const { team } = attrs;

const keyFigureStats = [
  {
    id: 1,
    name: 'Active projects',
    stat: props.teamProjectsActiveCountForLastTwoPeriods.current_period_count,
    statPrevious: props.teamProjectsActiveCountForLastTwoPeriods.previous_period_count,
    icon: FolderOpenIcon,
  },
  {
    id: 2,
    name: 'Proposals created',
    stat: props.teamProposalsCreatedCountForLastTwoPeriods.current_period_count,
    statPrevious: props.teamProposalsCreatedCountForLastTwoPeriods.previous_period_count,
    icon: DocumentPlusIcon,
  },
  {
    id: 3,
    name: 'Accepted proposals',
    stat: props.teamProposalsVersionsAcceptedCountForLastTwoPeriods.current_period_count,
    statPrevious: props.teamProposalsVersionsAcceptedCountForLastTwoPeriods.previous_period_count,
    icon: DocumentCheckIcon,
  },
];

const showCreateForm = ref(false);
</script>
